import { signIn, signOut } from '../app/utils';
import { returnCurrencyObject } from '../../utils/currency';
import { FinancialSummaryInfo } from '../approval/types';

export const stateForTests = {
  userData: {
    selectedCountryId: 'US',
  },
  flight: {
    fyData: [
      {
        fiscalYear: 'FY22',
        countries: ['US'],
      },
    ],
    currentFy: 'FY22',
    countries: 'US',
  },
};
export const returnSnackbarMessage = (error, key) => {
  if (error?.response?.status === 400) {
    return error?.response?.data?.message || key;
  }
  return key;
};

export const checkForUnauthorized = async (err) => {
  if (err?.response?.status === 401 || err?.response?.status === 403) {
    await signOut();
    await signIn();
  } else if (err?.response?.status === 400) {
    throw new Error(err);
  } else {
    return err;
  }
};

export interface ReduxAction {
  type: string;
  payload: any;
}

const ERROR_STATUSES = [404, 500, 409, 429];

export const checkForHTTPError = (response) => {
  if (ERROR_STATUSES.includes(response?.status)) {
    throw new Error('HTTP error');
  }
};
export const returnApiUrl = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_API_URL}/`
    : '/api/v1/';
export const returnAnalyticsApi = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_ANALYTICS_API_URL}/`
    : '/api/analytics/';
export const returnEtrApi = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_ETR_API_URL}/`
    : '/etr/';
export const returnMSTeamsApi = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_MSTEAMS_API_URL}/`
    : '/msteams/';
export const returnMsGraphApi = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_MS_GRAPH_API_URL}/`
    : '/ms-graph/';
export const returnFollowUpApiUrl = () =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_FOLLOWUP_API_URL}`
    : '/api/follow-up/';

export const formatToThousands = (num: number) => {
  if (num) {
    return Math.round((num / 1000) * 10) / 10;
  }
  return num;
};
export const formatToOriginal = (num: number) => {
  if (num) {
    return num * 1000;
  }
  return num;
};
export const FINANCIAL_INFO_ROWS = [
  'totalBudgetAllFY',
  'totalBudget',
  'totalLand',
  'totalProperty',
  'totalRetail',
  'totalRetailCost',
];

export const FINANCIAL_SUMMARY_INFO_ROWS = [
  'totalActuals',
  'totalForecast',
  'totalReleased',
];
export const INVESTMENTS_ROWS = [
  'landCost',
  'propertyInvestment',
  'retailCost',
  'retailInvestment',
];

export const FUTURE_FORECAST_FYS = ['FY27', 'FY28', 'FY29'];

export const returnFormattedForecastsForFutureFys = (project, currencies) => {
  const result: Record<string, number> = {};
  FUTURE_FORECAST_FYS.forEach((fy) => {
    const forecast = project?.financialSummaryInfos?.find(
      (info: FinancialSummaryInfo) =>
        info.fiscalYear === fy && info.financialCategory === 'FORECAST',
    );
    const currencyRate =
      returnCurrencyObject(currencies, project?.countryCode, fy)
        ?.currencyRate || 1;
    result[`forecastLandCost${fy}`] = forecast
      ? formatToThousands(forecast.landCost * currencyRate)
      : 0;
    result[`forecastPropertyInvestment${fy}`] = forecast
      ? formatToThousands(forecast.propertyInvestment * currencyRate)
      : 0;
    result[`forecastRetailInvestment${fy}`] = forecast
      ? formatToThousands(forecast.retailInvestment * currencyRate)
      : 0;
    result[`forecastRetailCost${fy}`] = forecast
      ? formatToThousands(forecast.retailCost * currencyRate)
      : 0;
  });
  return result;
};

export const mapFinancialInfo = (financialInfo) => {
  if (financialInfo) {
    const financialsCopy = { ...financialInfo };
    FINANCIAL_INFO_ROWS.forEach((row) => {
      financialsCopy[row] = formatToThousands(financialInfo[row]);
    });
    return financialsCopy;
  }
  return financialInfo;
};

export const mapFinancialSummaryInfo = (financialSummaryInfo) => {
  if (financialSummaryInfo) {
    const financialsCopy = { ...financialSummaryInfo };
    FINANCIAL_SUMMARY_INFO_ROWS.forEach((row) => {
      financialsCopy[row] = formatToThousands(financialSummaryInfo[row]);
    });
    return financialsCopy;
  }
  return financialSummaryInfo;
};

export const formatInvestments = (investments) =>
  (investments || []).map((investment) => {
    const copy = { ...investment };
    INVESTMENTS_ROWS.forEach((row) => {
      copy[row] = formatToThousands(copy[row]);
    });
    return copy;
  });

export const mapInvestmentsToOriginal = (investments) =>
  investments.map((investment) => {
    const copy = { ...investment };
    INVESTMENTS_ROWS.forEach((row) => {
      copy[row] = formatToOriginal(investment[row]);
    });
    return copy;
  });
